// =============================================================================
// Portal Header — Top bar with page title and user menu
// =============================================================================

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import {
  Avatar,
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '@tkpod/ui';
import { NotificationBell } from '@/components/notifications/NotificationBell';

interface HeaderProps {
  onMenuClick: () => void;
}

const pageTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/orders': 'Orders',
  '/products': 'My Products',
  '/stores': 'Stores',
  '/billing': 'Billing',
  '/settings': 'Settings',
  '/profile': 'Profile',
};

export function Header({ onMenuClick }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchOrganization } = useAuth();
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const organizations = user?.organizations ?? [];
  const hasMultipleOrgs = organizations.length > 1;

  const handleSwitchOrganization = async (organizationId: string) => {
    if (
      switchingOrgId !== null ||
      organizationId === user?.currentOrganization?.id
    ) {
      return;
    }
    setSwitchingOrgId(organizationId);
    try {
      await switchOrganization(organizationId);
      // Full reload: every org-scoped query (orders, products, wallet…)
      // must refetch under the new organization context.
      window.location.assign('/dashboard');
    } catch {
      setSwitchingOrgId(null);
    }
  };

  // Prefer an exact match; otherwise fall back to the first path segment so
  // nested routes (e.g. `/products/:id`, `/orders/:id`) still show the
  // correct section title in the header.
  const title =
    pageTitles[location.pathname] ??
    pageTitles[`/${location.pathname.split('/').filter(Boolean)[0] ?? ''}`] ??
    'Portal';

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-surface px-6">
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          type="button"
          onClick={onMenuClick}
          className="rounded-md p-2 text-text-secondary hover:bg-surface-tertiary lg:hidden"
          aria-label="Open sidebar"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        {/* Page title */}
        <h1 className="text-lg font-semibold text-text-primary">{title}</h1>
      </div>

      {/* Right side — Notifications + User menu */}
      <div className="flex items-center gap-1">
        <NotificationBell />

        <DropdownMenu align="right">
          <DropdownTrigger className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2">
            <Avatar name={displayName} size="sm" />
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-sm font-medium text-text-primary truncate max-w-[140px]">
                {displayName}
              </p>
              {user?.currentOrganization && (
                <p className="text-[11px] leading-tight text-text-tertiary truncate max-w-[140px]">
                  {user.currentOrganization.role}
                </p>
              )}
            </div>
            <svg className="h-4 w-4 shrink-0 text-text-tertiary" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </DropdownTrigger>

          <DropdownContent minWidth="min-w-[240px]">
            <div className="px-3 py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <Avatar name={displayName} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-text-primary truncate">{displayName}</p>
                  <p className="text-xs text-text-secondary truncate">{user?.email}</p>
                </div>
              </div>
              {user?.currentOrganization && !hasMultipleOrgs && (
                <div className="mt-2.5 flex items-center gap-2 rounded-md bg-surface-secondary px-2.5 py-1.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-success" />
                  <span className="text-xs text-text-secondary truncate">
                    {user.currentOrganization.name}
                  </span>
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                    {user.currentOrganization.role}
                  </span>
                </div>
              )}
            </div>

            {hasMultipleOrgs && (
              <>
                <div className="px-3 pt-2 pb-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Organizations
                  </p>
                </div>
                <div className="max-h-56 overflow-y-auto py-1" role="group" aria-label="Switch organization">
                  {organizations.map((org) => {
                    const isActive = org.id === user?.currentOrganization?.id;
                    const isSwitching = switchingOrgId === org.id;
                    return (
                      <button
                        key={org.id}
                        type="button"
                        disabled={switchingOrgId !== null}
                        onClick={() => void handleSwitchOrganization(org.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-secondary disabled:cursor-default ${
                          isActive ? 'bg-surface-secondary/60' : ''
                        }`}
                      >
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            isActive ? 'bg-success' : 'bg-border'
                          }`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-text-primary">{org.name}</span>
                          <span className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                            {org.role}
                          </span>
                        </span>
                        {isSwitching ? (
                          <span className="text-[10px] text-text-tertiary">Switching…</span>
                        ) : isActive ? (
                          <CheckIcon />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                <DropdownSeparator />
              </>
            )}

            <div className="py-1">
              <DropdownItem
                icon={<UserIcon />}
                onClick={() => navigate('/profile')}
              >
                Profile
              </DropdownItem>

              <DropdownItem
                icon={<SettingsIcon />}
                onClick={() => navigate('/settings')}
              >
                Settings
              </DropdownItem>
            </div>

            <DropdownSeparator />

            <div className="py-1">
              <DropdownItem
                icon={<LogoutIcon />}
                danger
                onClick={handleLogout}
              >
                Sign Out
              </DropdownItem>
            </div>
          </DropdownContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function UserIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
    </svg>
  );
}
